import { Bell, List as ListIcon, Plus, WarningCircle } from "@phosphor-icons/react";
import { Button, Sheet, SheetContent, SheetTrigger } from "@stylingcv/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { axios } from "@/client/libs/axios";
import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useCentralizedTokens } from "@/client/hooks/use-centralized-tokens";
import { useLocalizedNavigation } from "../../hooks/useLocalizedNavigation";

import { StylionAtmosphere } from "@/client/components/stylion-atmosphere";

import { Sidebar } from "./_components/sidebar";

const PAGE_TITLES: Record<string, string> = {
  "/dashboard/resumes": "Dashboard",
  "/dashboard/job-search": "Job Search",
  "/dashboard/job-tracker": "Job Tracker",
  "/dashboard/smart-generator": "AI Generator",
  "/dashboard/interview": "Interview",
  "/dashboard/interview/voice": "Voice Interview",
  "/ats-score-checker": "ATS Checker",
  "/dashboard/profile": "My Profile",
  "/dashboard/settings": "Settings",
  "/dashboard/help": "Help & Support",
  "/dashboard/ai-assistant": "AI Assistant",
  "/pricing": "Pricing",
};

export const DashboardLayout = () => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState(false);
  const location = useLocation();
  const { getLocalizedHref } = useLocalizedNavigation();
  const { tokenBalance } = useCentralizedTokens();

  // Derive page title from current path (strip locale prefix)
  const rawPath = location.pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, "") || location.pathname;
  const pageTitle = PAGE_TITLES[rawPath] ?? "Dashboard";

  // Check if we're in RTL mode
  const isRTL = document.documentElement.dir === "rtl";

  const { data: subscription } = useQuery<{
    status?: string;
    provider?: string;
  } | null>({
    queryKey: ["dashboard", "subscription-status"],
    queryFn: async () => {
      const response = await axios.get("/payment/subscription");
      return response.data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const isPastDue = subscription?.status === "past_due";

  const handleUpdatePaymentMethod = async () => {
    try {
      setUpdatingPaymentMethod(true);
      const response = await axios.post("/payment/create-payment-update-session");
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } finally {
      setUpdatingPaymentMethod(false);
    }
  };

  return (
    <div className="stylion-app relative flex min-h-screen">
      <StylionAtmosphere />
      {/* Desktop Sidebar - Fixed to left edge */}
      <aside
        className={`hidden transition-all duration-300 ease-in-out lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:flex-col ${
          collapsed ? "lg:w-[80px]" : "lg:w-[260px]"
        } ${
          isRTL ? "lg:right-0" : "lg:left-0"
        }`}
      >
        <Sidebar collapsed={collapsed} onToggleCollapse={() => setCollapsed(!collapsed)} />
      </aside>

      {/* Desktop Top Header Bar */}
      <header
        className={`stylion-app-shell fixed top-0 right-0 z-30 hidden h-14 items-center justify-between border-b px-6 transition-all duration-300 ease-in-out lg:flex ${
          isRTL
            ? collapsed ? "right-0 left-[80px]" : "right-0 left-[260px]"
            : collapsed ? "left-[80px] right-0" : "left-[260px] right-0"
        }`}
      >
        <span className="text-[15px] font-semibold text-[var(--stylion-ink)]">{pageTitle}</span>
        <div className="flex items-center gap-3">
          {/* Token Count */}
          <div className="flex items-center gap-1.5 rounded-full border border-black/5 bg-black/[0.03] px-3 py-1.5">
            <span className="text-sm font-bold text-primary">{tokenBalance.toLocaleString()}</span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Tokens
            </span>
          </div>
          {/* More Tokens Button */}
          <a
            href={getLocalizedHref("/pricing?tab=tokens")}
            className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
          >
            <Plus size={12} weight="bold" />
            More
          </a>
          {/* Bell */}
          <button className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-black/5 hover:text-[var(--stylion-ink)]">
            <Bell size={18} weight="regular" />
          </button>
          {/* Avatar */}
          <UserOptions>
            <button className="flex size-8 shrink-0 items-center justify-center rounded-full ring-2 ring-primary/30 transition-opacity hover:opacity-80">
              <UserAvatar size={32} className="rounded-full" />
            </button>
          </UserOptions>
        </div>
      </header>

      {/* Mobile Header */}
      <div className="stylion-app-shell fixed inset-x-0 top-0 z-50 flex items-center justify-between border-b px-4 py-3 lg:hidden">
        <Logo size={160} className="h-9 w-auto max-w-[160px]" />

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button size="icon" variant="ghost">
              <ListIcon size={24} />
            </Button>
          </SheetTrigger>

          <SheetContent
            showClose={false}
            side={isRTL ? "right" : "left"}
            className="!w-[260px] gap-0 border-none bg-transparent p-0 shadow-none focus-visible:outline-none"
            dir={isRTL ? "rtl" : "ltr"}
            style={{ width: 260 }}
          >
            <Sidebar setOpen={setOpen} />
          </SheetContent>
        </Sheet>
      </div>

      {/* Main Content */}
      <main
        className={`relative z-10 flex min-h-screen flex-1 flex-col pt-[60px] transition-all duration-300 ease-in-out lg:pt-14 ${
          isRTL
            ? collapsed ? "lg:mr-[80px]" : "lg:mr-[260px]"
            : collapsed ? "lg:ml-[80px]" : "lg:ml-[260px]"
        }`}
      >
        {isPastDue && (
          <div className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-2">
                <WarningCircle className="mt-0.5 size-5 shrink-0" weight="fill" />
                <div className="text-sm">
                  Your subscription payment is past due. Your access is still active during grace
                  period, but please update billing now to avoid interruption.
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleUpdatePaymentMethod}
                disabled={updatingPaymentMethod}
              >
                {updatingPaymentMethod ? "Opening..." : "Update Billing"}
              </Button>
            </div>
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </div>
        <footer className="stylion-app-shell mt-auto shrink-0 border-t px-4 py-3 dark:border-white/10">
          <div className="flex items-center justify-end">
            <ThemeSwitch />
          </div>
        </footer>
      </main>
    </div>
  );
};
